/**
 * Expectativa canónica de experiencia visitor por país — una sola fuente adaptable.
 *
 * Deriva template + locale del WORKER_COUNTRY_TEMPLATE_MAP (espejo de worker.js).
 * Usado por: geo sim toolbar, admin overview, regresiones build.
 *
 * Reglas:
 *  - AR → BaseTemplate + español (provincia controla hero CTA)
 *  - Resto → USExclusiveLanding por default del mapa (override admin vía KV/DB)
 *  - Home visitor siempre `/` (no `/en` SEO)
 *  - Idioma: locale del mapa salvo override explícito `?lang=` en simulación
 */

import {
  WORKER_COUNTRY_TEMPLATE_MAP,
  WORKER_DEFAULT_TEMPLATE,
  getWorkerTemplateConfigForCountry,
} from "@/config/workerCountryTemplateMap";
import {
  FALLBACK_LANGUAGE,
  isLanguageSupported,
  type SupportedLanguage,
} from "@/lib/languages";
import { getServerTemplateConfig } from "@/lib/serverTemplateDetection";

export interface VisitorExperienceExpectation {
  country: string;
  templateName: string;
  language: string;
  languageBase: SupportedLanguage;
  homePath: "/";
  isArgentina: boolean;
  usesUsExclusiveLanding: boolean;
}

export function normalizeCountryCodeInput(
  code: string | null | undefined,
): string | null {
  if (code == null || typeof code !== "string") return null;
  const t = code.trim().toUpperCase();
  if (!t || t === "UNKNOWN" || t.length < 2) return null;
  return t.slice(0, 2);
}

/** Mapa worker → expectativa visitor (101 países + DEFAULT implícito). */
export function getVisitorExperienceExpectation(
  countryCode: string,
): VisitorExperienceExpectation {
  const country = normalizeCountryCodeInput(countryCode) ?? "US";
  const row = WORKER_COUNTRY_TEMPLATE_MAP[country] ?? WORKER_DEFAULT_TEMPLATE;
  const cfg = getWorkerTemplateConfigForCountry(country);
  const language = cfg.language || row.language || WORKER_DEFAULT_TEMPLATE.language;
  const rawBase = language.split("-")[0].toLowerCase();
  const languageBase: SupportedLanguage = isLanguageSupported(rawBase)
    ? (rawBase as SupportedLanguage)
    : FALLBACK_LANGUAGE;

  return {
    country,
    templateName: row.template,
    language,
    languageBase,
    homePath: "/",
    isArgentina: country === "AR",
    usesUsExclusiveLanding: row.template === "USExclusiveLanding",
  };
}

/** Etiqueta humana para admin / geo sim (mapa, no worker emitido). */
export function formatVisitorExperienceLabel(countryCode: string): string {
  const exp = getVisitorExperienceExpectation(countryCode);
  if (exp.isArgentina) {
    return `${exp.templateName} · español (provincia controla CTA)`;
  }
  return `${exp.templateName} · ${exp.language}`;
}

/** Lista ordenada de todos los países del mapa (adaptable al agregar/quitar en worker). */
export function listWorkerCountryCodes(): string[] {
  return Object.keys(WORKER_COUNTRY_TEMPLATE_MAP).sort();
}

/**
 * Base de idioma para dual-CTA / labels: prioriza __TEMPLATE_CONFIG__.language,
 * luego mapa por país, luego fallback EN.
 */
export function resolveWorkerLanguageBase(countryFallback?: string): SupportedLanguage {
  const cfg = getServerTemplateConfig();
  const tag = (cfg?.language || "").trim();
  if (tag) {
    const base = tag.split("-")[0].toLowerCase();
    if (isLanguageSupported(base)) return base as SupportedLanguage;
    return FALLBACK_LANGUAGE;
  }
  const code =
    normalizeCountryCodeInput(countryFallback) ||
    normalizeCountryCodeInput(cfg?.country) ||
    null;
  if (code) {
    return getVisitorExperienceExpectation(code).languageBase;
  }
  return FALLBACK_LANGUAGE;
}

/** Geo sim visitor: home `/`; admin conserva su ruta. */
export function resolveVisitorSimulationPath(currentPath: string): string {
  return currentPath.startsWith("/admin") ? currentPath : "/";
}

export function emittedTemplateMatchesExpectation(
  emittedTemplate: string | null | undefined,
  countryCode: string,
): boolean {
  if (!emittedTemplate) return false;
  return emittedTemplate === getVisitorExperienceExpectation(countryCode).templateName;
}